/**
 * The pipeline every Chrome tool call flows through.
 *
 * A command author writes four small functions (`args`, `resolve`, `diff`,
 * `run`). This module owns everything between Chrome's `execute` callback and
 * those four functions: readiness, validation, tenant scope, the dirty-state
 * veto, authorization, abort, logging, and output encoding.
 *
 * Three facts about Chrome shape most of this file.
 *
 *   1. Chrome validates nothing. `inputSchema` is advice to the model, not a
 *      gate, so the command's zod schema is the only enforced contract.
 *   2. Chrome coerces whatever `execute` resolves with. A string reaches the
 *      agent unquoted, an object is `JSON.stringify`d, and an empty result is
 *      silently replaced with the literal "Operation succeeded". So this
 *      runtime always resolves with a non-empty string it built itself.
 *   3. Registration happens at module load, before React mounts. The first
 *      second of app boot is a real window in which a tool can be called
 *      before the capability layer exists and before the workspace selection
 *      has been reconciled. Both are waited for, briefly and boundedly, then
 *      refused retryably. Nothing here can hang.
 *
 * Refusals are VALUES. Exceptions are reserved for bugs, and even a bug is
 * caught at the boundary so a thrown service error cannot escape into Chrome.
 */

import { z } from 'zod';
import {
  computePatchDigest,
  dirtyVeto,
  isRefusal,
  patchFromDiff,
  readBaseVersion,
  refuse,
  renderPatchValue,
  type AnyCommand,
  type Command,
  type CommandName,
  type CommitAuthorization,
  type Consequence,
  type Diff,
  type ReadDeps,
  type Receipt,
  type Refusal,
  type TenantId,
  type WriteDeps,
} from './contract';
import type { ToolBody } from './install';
import { publishWitnessSettle } from './witness/store';
import {
  activeTenantIdFromSelection,
  useWorkspaceStore,
  type ClientSelection,
} from '../stores/useWorkspaceStore';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

/** Bounded wait for the boot window. Never a retry loop, never unbounded. */
const DEFAULT_BOOT_WINDOW_MS = 3000;

/** An authorization outlives one `run`, not one session. */
const DEFAULT_AUTH_TTL_MS = 30000;

/** Chrome's own guidance for tool output. Enforced with a visible notice. */
const DEFAULT_MAX_OUTPUT_CHARS = 1500;

const MAX_ISSUES_REPORTED = 5;
const MAX_CANDIDATES_REPORTED = 10;
const MAX_FACT_CHARS = 200;
const MAX_EXAMPLE_CHARS = 320;

/** Module load time. This is when Chrome registration happens. */
const MODULE_LOADED_AT = Date.now();

// ---------------------------------------------------------------------------
// Chrome's execute surface
// ---------------------------------------------------------------------------

/**
 * The second argument Chrome hands `execute`.
 *
 * `Partial` on purpose. The real IDL marks `signal` required, and
 * `webmcp.d.ts` declares it that way, but the shape is not stable across
 * Chrome builds and a missing signal must degrade to "never aborts" rather
 * than throw on a property read.
 */
export type ToolExecuteContext = Partial<WebMCP.ToolExecuteCallbackOptions>;

/** What Chrome calls. Always resolves, always with a non-empty string. */
export type ToolExecute = (args: unknown, context?: ToolExecuteContext) => Promise<string>;

// ---------------------------------------------------------------------------
// Logging
//
// A closed field shape is the enforcement of "never log arguments, results, or
// service responses". There is no slot to put a payload in, so a future author
// cannot leak one without changing this type on purpose.
// ---------------------------------------------------------------------------

export type PipelineState =
  | 'readiness'
  | 'validate'
  | 'scope'
  | 'resolve'
  | 'diff'
  | 'focus'
  | 'authorize'
  | 'run'
  | 'encode';

export type PipelineOutcome = 'ok' | 'refused' | 'cancelled' | 'failed';

export interface LogFields {
  /**
   * The command an entry is about. Optional only because the write boundary
   * logs below the pipeline, where a seam name is the identity that exists and
   * a command name is not in scope.
   */
  readonly tool?: CommandName;
  /** The write seam, for the entries `wrapWriteSeams` emits. */
  readonly seam?: WriteSeamName;
  readonly state?: PipelineState;
  readonly outcome?: PipelineOutcome;
  readonly durationMs?: number;
  /** A count of something, never the something itself. */
  readonly count?: number;
  readonly consequence?: Consequence;
  readonly reachesGeneratedContent?: boolean;
  /** Constructor name only. A message can embed a service response. */
  readonly errorName?: string;
}

export interface RuntimeLogger {
  debug(message: string, fields: LogFields): void;
  warn(message: string, fields: LogFields): void;
  error(message: string, fields: LogFields): void;
}

/**
 * TODO(integration): `frontend/src/utils/logger.ts` is the app logger and is a
 * one line adapter away. It is not imported here so this module typechecks
 * without Vite's `import.meta.env` types.
 */
const consoleLogger: RuntimeLogger = {
  debug: (message, fields) => console.debug(`[webmcp] ${message}`, fields),
  warn: (message, fields) => console.warn(`[webmcp] ${message}`, fields),
  error: (message, fields) => console.error(`[webmcp] ${message}`, fields),
};

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/**
 * The dependency pair a command is run against.
 *
 * TODO(capability worker): the module that builds `ReadDeps` and `WriteDeps`
 * over the brand bible store calls `installCapabilities` once, from a React
 * effect, after the store is usable. Everything before that call falls into
 * the boot window path below.
 */
export interface Capabilities {
  readonly read: ReadDeps;
  readonly write: WriteDeps;
}

let installedCapabilities: Capabilities | null = null;
let capabilityWaiters: Array<(caps: Capabilities) => void> = [];

/**
 * Idempotent. Late waiters are released in registration order.
 *
 * THE WRITE SIDE IS WRAPPED HERE, and this is the only door. Whatever a caller
 * hands in, what gets stored is a `WriteDeps` whose every seam spends its
 * authorization against this module's registry before it delegates. No other
 * `buildCapabilities`, no test double and no future lane has a path by which an
 * unguarded write seam becomes installed.
 */
export function installCapabilities(caps: Capabilities): void {
  const guarded: Capabilities = { read: caps.read, write: wrapWriteSeams(caps.write) };
  installedCapabilities = guarded;
  const waiting = capabilityWaiters;
  capabilityWaiters = [];
  for (let i = 0; i < waiting.length; i += 1) {
    const release = waiting[i];
    if (release) release(guarded);
  }
}

/** Teardown and tests. Calls after this refuse retryably, they do not hang. */
export function uninstallCapabilities(): void {
  installedCapabilities = null;
}

function awaitCapabilities(
  deadline: number,
  signal: AbortSignal,
  clock: () => number,
): Promise<Capabilities | null> {
  if (installedCapabilities) return Promise.resolve(installedCapabilities);
  const remaining = deadline - clock();
  if (remaining <= 0 || signal.aborted) return Promise.resolve(null);

  return new Promise<Capabilities | null>((resolve) => {
    let settled = false;
    const finish = (value: Capabilities | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      const index = capabilityWaiters.indexOf(onInstall);
      if (index >= 0) capabilityWaiters.splice(index, 1);
      resolve(value);
    };
    const onInstall = (caps: Capabilities): void => finish(caps);
    const onAbort = (): void => finish(null);

    const timer = setTimeout(() => finish(null), remaining);
    capabilityWaiters.push(onInstall);
    signal.addEventListener('abort', onAbort);
  });
}

// ---------------------------------------------------------------------------
// Scope
//
// The tenant is derived from the per tab selection exactly once per call and
// then threaded through resolve, diff, authorize, and run. It is deliberately
// NOT re-read later: the operator can switch client mid call, and a call that
// resolved against one client must never commit against another.
// ---------------------------------------------------------------------------

/**
 * Waits for the very first selection, and only inside the true boot window.
 *
 * `null` before ProtectedRoute reconciles is a transient state, so refusing
 * instantly during boot would be a false negative. Once the boot window has
 * passed, `null` means the operator genuinely has no client selected and the
 * refusal is immediate.
 */
function awaitInitialSelection(
  deadline: number,
  signal: AbortSignal,
  clock: () => number,
): Promise<ClientSelection> {
  const immediate = useWorkspaceStore.getState().selection;
  if (immediate) return Promise.resolve(immediate);

  const remaining = deadline - clock();
  if (remaining <= 0 || signal.aborted) return Promise.resolve(null);

  return new Promise<ClientSelection>((resolve) => {
    let settled = false;
    const finish = (value: ClientSelection): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      unsubscribe();
      resolve(value);
    };
    const onAbort = (): void => finish(null);

    const timer = setTimeout(() => finish(null), remaining);
    const unsubscribe = useWorkspaceStore.subscribe((state) => {
      if (state.selection) finish(state.selection);
    });
    signal.addEventListener('abort', onAbort);
  });
}

/**
 * The ONLY place a `TenantId` is minted. Everything downstream receives the
 * branded value and never touches the store again.
 *
 * Null is a refusal. It is never a fallback, never the AgileCo test tenant,
 * and never the first workspace in the list.
 */
export async function requireScope(
  deadline: number,
  signal: AbortSignal,
  clock: () => number = Date.now,
): Promise<TenantId | Refusal> {
  const selection = await awaitInitialSelection(deadline, signal, clock);
  const derived = activeTenantIdFromSelection(selection);
  const website = typeof derived === 'string' ? derived.trim() : '';

  if (website.length === 0) {
    if (selection === null) {
      return refuse(
        'No client is selected in this browser tab, so there is no tenant to act on. ' +
          'Ask the operator to pick a client in the workspace switcher, then call this tool again.',
        { retryable: true },
      );
    }
    return refuse(
      'The selected workspace has no website configured, so it has no data plane to read or write. ' +
        'Ask the operator to set the workspace website, or to switch to a client that has one.',
      { retryable: false },
    );
  }

  // The single mint site. Do not copy this cast anywhere else.
  return website as TenantId;
}

// ---------------------------------------------------------------------------
// Authorization
//
// There is no confirmation dialog by standing project decision, so this is not
// a consent gate. It is a binding: an authorization minted for one tenant,
// table, row, and patch cannot be replayed against another, and it is spent
// exactly once.
//
// The digest and the value rendering both live in `contract.ts`. They used to
// be implemented here AND in the writer, which meant the two sides of the
// binding compared different numbers and every write refused.
// ---------------------------------------------------------------------------

interface AuthorizationRecord {
  readonly auth: CommitAuthorization;
  consumed: boolean;
}

let liveAuthorizations: AuthorizationRecord[] = [];

function mintAuthorization(
  target: {
    readonly tenant: TenantId;
    readonly table: string;
    readonly rowId: string | null;
    readonly patchDigest: string;
    readonly baseVersion: string | null;
  },
  ttlMs: number,
  clock: () => number,
): AuthorizationRecord {
  const now = clock();
  const auth: CommitAuthorization = {
    __brand: 'CommitAuthorization',
    tenant: target.tenant,
    table: target.table,
    rowId: target.rowId,
    patchDigest: target.patchDigest,
    baseVersion: target.baseVersion,
    expiresAt: now + ttlMs,
  };
  // Drop spent and expired records rather than sweeping on a timer.
  liveAuthorizations = liveAuthorizations.filter(
    (record) => !record.consumed && record.auth.expiresAt > now,
  );
  const record: AuthorizationRecord = { auth, consumed: false };
  liveAuthorizations.push(record);
  return record;
}

export interface CommitTarget {
  /**
   * The client the write is ACTUALLY about to land on, read at issue time.
   * Deliberately a plain string: this is the value being checked against the
   * branded one, not a second place a `TenantId` is minted.
   */
  readonly tenant: string;
  readonly table: string;
  readonly rowId: string | null;
  /** `computePatchDigest` over the exact patch about to be sent. */
  readonly patchDigest: string;
}

/**
 * Spends an authorization against the exact write about to happen. Returns a
 * refusal on any mismatch and `null` when the write may proceed.
 *
 * Called from exactly one place, `wrapWriteSeams`, which wraps every seam of
 * every installed `WriteDeps`. Consumption is therefore structural rather than
 * a rule a seam author has to remember, and a seam file that carries its own
 * consumption mechanism (a `WeakSet`, say) is not just redundant, it is wrong:
 * it proves membership in itself instead of in this registry.
 */
export function consumeAuthorization(
  auth: CommitAuthorization,
  target: CommitTarget,
  clock: () => number = Date.now,
): Refusal | null {
  const record = liveAuthorizations.find((candidate) => candidate.auth === auth);
  if (!record) {
    return refuse(
      'This write was not authorized by the current tool call. Start the command again.',
    );
  }
  if (record.consumed) {
    return refuse(
      'That authorization was already used. Each approved change commits exactly once, so start the command again.',
    );
  }
  // Re-checked HERE, at the moment of the write, and not only at the mint. The
  // gap between the two is real work: the Brand Bible writer settles its store
  // first, and a slow settle can outlive the whole TTL.
  if (clock() > auth.expiresAt) {
    record.consumed = true;
    return refuse('That authorization expired before the write ran. Start the command again.', {
      retryable: true,
    });
  }

  const mismatch =
    target.tenant !== auth.tenant
      ? 'client'
      : target.table !== auth.table
        ? 'table'
        : target.rowId !== auth.rowId
          ? 'row'
          : target.patchDigest !== auth.patchDigest
            ? 'change'
            : null;

  if (mismatch) {
    // Burn it. A binding mismatch is never transient, and a live authorization
    // left behind after one is exactly the replay this exists to prevent.
    record.consumed = true;
    return refuse(
      `The write does not match what was authorized (the ${mismatch} differs). Nothing was written. Start the command again.`,
    );
  }

  record.consumed = true;
  return null;
}

// ---------------------------------------------------------------------------
// The write boundary
//
// CONSUMPTION IS STRUCTURAL. Every write seam an installed capability supplies
// is wrapped here, once, so a lane cannot forget to spend its authorization and
// cannot substitute a parallel mechanism that never touches the registry above.
// A seam file contains no authorization code at all, which is the point: there
// is nowhere left for it to get this wrong.
// ---------------------------------------------------------------------------

/** What a seam is about to write, read from the seam's OWN arguments. */
interface SeamWrite {
  readonly rowId: string | null;
  readonly patch: Readonly<Record<string, unknown>>;
}

type WriteSeamName = keyof WriteDeps;
type SeamBinding<K extends WriteSeamName> = (
  ...args: Parameters<NonNullable<WriteDeps[K]>>
) => SeamWrite;

/**
 * The argument-to-target binding for every write seam.
 *
 * EXHAUSTIVE BY TYPE. A seam added to `WriteDeps` fails this object to compile
 * until it is bound, so a new write path cannot reach the network without
 * someone stating what it writes.
 *
 * A binding MUST reproduce the row and the patch that the command's own `diff`
 * declared, because the authorization digest was minted from that diff. Getting
 * it wrong refuses every call through that seam, loudly and before the request:
 * the same fail-closed direction as `readBaseVersion` and `computePatchDigest`
 * having exactly one implementation each.
 *
 * `null` means no binding is settled. The seam then refuses, because a write
 * nobody can check against its approval is not a write this surface makes.
 */
const SEAM_BINDINGS: { readonly [K in WriteSeamName]: SeamBinding<K> | null } = {
  // The patch IS the argument, so this binding is the identity on it.
  commit: (auth, patch) => ({ rowId: auth.rowId, patch }),
  // `plannerSetCalendarDate.diff` declares one `scheduled_date` change whose
  // `to` is this same string.
  setCalendarDate: (_auth, articleId, date) => ({
    rowId: articleId,
    patch: { scheduled_date: date },
  }),
  // `plannerScheduleWpPublish.diff` declares one `scheduled_publish_time`
  // change whose `to` is this same `toISOString`.
  scheduleWpPublish: (_auth, articleId, publishAt) => ({
    rowId: articleId,
    patch: { scheduled_publish_time: publishAt.toISOString() },
  }),
  // No command reaches these yet, so there is no diff to bind against. The lane
  // that lands one writes its binding here, in the same edit that installs it.
  // `plannerCreateArticle.diff` declares `keyword` and, when supplied, `title`.
  createArticle: (_auth, draft) => ({
    rowId: null,
    patch: draft.title === draft.keyword ? { keyword: draft.keyword } : { keyword: draft.keyword, title: draft.title },
  }),
  // `plannerPublishArticles.diff` declares one `publish:<id>` change per id, and
  // the command caps ids at one because this authorization is single-use.
  publishArticle: (_auth, articleId) => ({
    rowId: articleId,
    patch: { [`publish:${articleId}`]: 'live on the client site' },
  }),
  // `plannerRestartGeneration.diff` declares exactly these two fields, which is
  // the entire point: neither may be inherited from the row.
  regenerate: (_auth, articleId, options) => ({
    rowId: articleId,
    patch: {
      model_override: options.modelOverride,
      wordpress_publish_mode: options.wordpressPublishMode,
    },
  }),
  // One authorization covers the whole batch, because it is consumed on the
  // first seam call and a per-row loop would refuse every row after the first.
  // `plannerApproveKeywords.diff` declares exactly these two fields, and the
  // id list is sorted so two orderings of the same set digest identically.
  approveKeyword: (_auth, keywordIds) => ({
    rowId: keywordIds.length === 1 ? keywordIds[0] : null,
    patch: { status: 'approved', ids: [...keywordIds].sort().join(',') },
  }),
  // Editor lane. Each editor command's `diff` declares one change whose `to` is
  // the same string these bindings reproduce, so the authorization digest and
  // the seam call agree. The row is always the content id, carried on the auth.
  // `edToggleBold`/`edToggleItalic` both declare `{ format: `toggle <mark>` }`.
  editorFormat: (auth, mark) => ({ rowId: auth.rowId, patch: { format: `toggle ${mark}` } }),
  // `edSaveDraft.diff` declares `{ save: 'draft' }`.
  editorSaveDraft: (auth) => ({ rowId: auth.rowId, patch: { save: 'draft' } }),
  // `edSetCanonical.diff` declares `{ canonical_url: <url> }`.
  editorSetCanonical: (auth, canonicalUrl) => ({ rowId: auth.rowId, patch: { canonical_url: canonicalUrl } }),
  // `edPublishCms.diff` declares `{ publish: 'live' }`.
  editorPublishCms: (auth) => ({ rowId: auth.rowId, patch: { publish: 'live' } }),
  // `edSetJsonld.diff` declares `{ schema_description: <text> }`.
  editorSetJsonldDescription: (auth, description) => ({
    rowId: auth.rowId,
    patch: { schema_description: description },
  }),
  // `edSetOgImage.diff` declares `{ og_image_url: <url> }`.
  editorSetOgImage: (auth, imageUrl) => ({ rowId: auth.rowId, patch: { og_image_url: imageUrl } }),
};

/** Structural, because the brand is a compile-time fiction at runtime. */
function isCommitAuthorization(value: unknown): value is CommitAuthorization {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { readonly __brand?: unknown }).__brand === 'CommitAuthorization' &&
    typeof (value as { readonly table?: unknown }).table === 'string'
  );
}

/**
 * The client this tab is on RIGHT NOW, never the one captured during `resolve`.
 *
 * The operator can switch client while a command is still running, and the
 * services below the seams resolve their own tenant when they are called. So
 * the drift that matters is measured here, at issue time, and it refuses
 * through the same one place every other binding mismatch does.
 */
function selectedTenantNow(): string | null {
  const derived = activeTenantIdFromSelection(useWorkspaceStore.getState().selection);
  const website = typeof derived === 'string' ? derived.trim() : '';
  return website.length === 0 ? null : website;
}

/**
 * Wraps every write seam so it spends its authorization immediately before it
 * delegates. Lane seam files need no change, and cannot opt out.
 */
export function wrapWriteSeams(write: WriteDeps, options: RuntimeOptions = {}): WriteDeps {
  const clock = options.now ?? Date.now;
  const log = options.logger ?? consoleLogger;
  const guarded: Record<string, unknown> = {};

  for (const name of Object.keys(write) as WriteSeamName[]) {
    const seam = write[name];
    if (typeof seam !== 'function') continue;

    // One erasure, at one boundary, for the whole map. The bindings above keep
    // their exact per-seam argument types; only this dispatch is generic.
    const bind = SEAM_BINDINGS[name] as ((...args: readonly unknown[]) => SeamWrite) | null;
    const call = seam as (...args: readonly unknown[]) => Promise<Receipt | Refusal>;

    guarded[name] = async (...args: readonly unknown[]): Promise<Receipt | Refusal> => {
      const auth = args[0];
      if (!isCommitAuthorization(auth)) {
        log.error('write seam called with no authorization', { seam: name });
        return refuse(`${name} was called without an authorization, so nothing was written.`);
      }
      if (!bind) {
        log.error('write seam has no authorization binding', { seam: name });
        return refuse(
          `${name} cannot be checked against what was approved in this build, so nothing was written.`,
        );
      }

      let target: SeamWrite;
      try {
        target = bind(...args);
      } catch {
        return refuse(
          `${name} was called with arguments that cannot be matched against the approved change, so nothing was written.`,
        );
      }

      const tenantNow = selectedTenantNow();
      if (tenantNow === null) {
        return refuse(
          'No client is selected in this browser tab any more, so there is nothing to write to. Nothing was written.',
          { retryable: true },
        );
      }

      const refusal = consumeAuthorization(
        auth,
        {
          tenant: tenantNow,
          table: auth.table,
          rowId: target.rowId,
          patchDigest: computePatchDigest(target.patch),
        },
        clock,
      );
      if (refusal) return refusal;

      return call(...args);
    };
  }

  // Every key came off `write` and every value carries that seam's own
  // signature, so this restores the shape the erasure dropped rather than
  // widening it.
  return guarded as unknown as WriteDeps;
}

// ---------------------------------------------------------------------------
// Validation
//
// Chrome validates nothing, so this is the whole contract. Chrome's own
// guidance is that a descriptive error lets a model self correct and retry, so
// every failure says what went wrong, what was expected, and shows one call
// that would work.
// ---------------------------------------------------------------------------

/**
 * A structurally read zod issue. Read this way rather than against zod's own
 * union so a zod minor that renames an issue code degrades to its `message`
 * instead of failing the build.
 */
interface RuntimeIssue {
  readonly code?: unknown;
  readonly path?: ReadonlyArray<string | number | symbol>;
  readonly message?: unknown;
  readonly expected?: unknown;
  readonly values?: ReadonlyArray<unknown>;
  readonly options?: ReadonlyArray<unknown>;
  readonly keys?: ReadonlyArray<unknown>;
  readonly format?: unknown;
  readonly origin?: unknown;
  readonly minimum?: unknown;
  readonly maximum?: unknown;
}

/** Optional, structurally checked. A command may carry one literal example. */
interface MaybeExampled {
  readonly example?: unknown;
}

function pathOf(issue: RuntimeIssue): string {
  const path = issue.path ?? [];
  if (path.length === 0) return 'the arguments object';
  let rendered = '';
  for (let i = 0; i < path.length; i += 1) {
    const segment = path[i];
    rendered += i === 0 ? String(segment) : `.${String(segment)}`;
  }
  return rendered;
}

function messageOf(issue: RuntimeIssue): string {
  return typeof issue.message === 'string' && issue.message.length > 0
    ? issue.message
    : 'the value is not acceptable';
}

function allowedValuesOf(issue: RuntimeIssue): ReadonlyArray<unknown> | null {
  const values = issue.values ?? issue.options;
  return values && values.length > 0 ? values : null;
}

function expectationOf(issue: RuntimeIssue): string {
  const allowed = allowedValuesOf(issue);
  if (allowed) {
    const shown = allowed.slice(0, 12).map((value) => JSON.stringify(value) ?? String(value));
    const suffix = allowed.length > shown.length ? ', and others' : '';
    return `one of ${shown.join(', ')}${suffix}`;
  }

  const code = typeof issue.code === 'string' ? issue.code : '';
  const origin = typeof issue.origin === 'string' ? issue.origin : 'value';

  switch (code) {
    case 'invalid_type':
      return issue.expected === undefined
        ? 'a different type'
        : `a value of type ${String(issue.expected)}`;
    case 'too_small':
      return issue.minimum === undefined
        ? `a larger ${origin}`
        : boundText(origin, 'at least', issue.minimum);
    case 'too_big':
      return issue.maximum === undefined
        ? `a smaller ${origin}`
        : boundText(origin, 'at most', issue.maximum);
    case 'invalid_format':
      return `a value in ${String(issue.format ?? 'the required')} format`;
    case 'unrecognized_keys':
      return 'only the documented fields, with no extra keys';
    default:
      return messageOf(issue);
  }
}

/** Says what a bound counts, so "at least 3" never reads as "3 of what". */
function boundText(origin: string, direction: string, bound: unknown): string {
  const value = String(bound);
  switch (origin) {
    case 'string':
      return `a string of ${direction} ${value} characters`;
    case 'array':
    case 'set':
    case 'file':
      return `${direction} ${value} items`;
    case 'date':
      return `a date ${direction === 'at least' ? 'on or after' : 'on or before'} ${value}`;
    default:
      return `a ${origin} of ${direction} ${value}`;
  }
}

function placeholderFor(issue: RuntimeIssue): unknown {
  const allowed = allowedValuesOf(issue);
  if (allowed && allowed.length > 0) return allowed[0];

  const expected = typeof issue.expected === 'string' ? issue.expected : 'string';
  switch (expected) {
    case 'number':
    case 'int':
    case 'bigint':
      return 1;
    case 'boolean':
      return true;
    case 'array':
      return [];
    case 'object':
    case 'record':
      return {};
    default:
      break;
  }

  if (issue.code === 'too_small' && typeof issue.minimum === 'number' && issue.minimum > 4) {
    return 'text'.padEnd(Math.min(issue.minimum, 40), 'x');
  }
  return 'text';
}

/** Builds the example container tree, using an array for a numeric segment. */
function setAtPath(
  root: Record<string, unknown>,
  path: ReadonlyArray<string | number | symbol>,
  value: unknown,
): void {
  if (path.length === 0) return;
  let cursor: Record<string, unknown> | unknown[] = root;

  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    const nextKey = path[i + 1];
    if (key === undefined) return;
    const container = typeof nextKey === 'number' ? [] : {};
    cursor = descend(cursor, key, container);
  }

  const last = path[path.length - 1];
  if (last === undefined) return;
  assign(cursor, last, value);
}

function descend(
  cursor: Record<string, unknown> | unknown[],
  key: string | number | symbol,
  fallback: Record<string, unknown> | unknown[],
): Record<string, unknown> | unknown[] {
  const existing = read(cursor, key);
  if (typeof existing === 'object' && existing !== null) {
    return existing as Record<string, unknown> | unknown[];
  }
  assign(cursor, key, fallback);
  return fallback;
}

function read(
  cursor: Record<string, unknown> | unknown[],
  key: string | number | symbol,
): unknown {
  return Array.isArray(cursor)
    ? cursor[typeof key === 'number' ? key : 0]
    : cursor[String(key)];
}

function assign(
  cursor: Record<string, unknown> | unknown[],
  key: string | number | symbol,
  value: unknown,
): void {
  if (Array.isArray(cursor)) {
    cursor[typeof key === 'number' ? key : 0] = value;
    return;
  }
  cursor[String(key)] = value;
}

function exampleCallFor(command: AnyCommand, issues: readonly RuntimeIssue[]): string {
  const authored = (command as unknown as MaybeExampled).example;
  if (authored !== undefined) return clip(safeJson(authored), MAX_EXAMPLE_CHARS);

  // Built only from schema facts and placeholders. The arguments the agent
  // actually sent are never echoed back.
  const draft: Record<string, unknown> = {};
  for (let i = 0; i < issues.length; i += 1) {
    const issue = issues[i];
    if (!issue) continue;
    setAtPath(draft, issue.path ?? [], placeholderFor(issue));
  }
  return clip(safeJson(draft), MAX_EXAMPLE_CHARS);
}

function safeJson(value: unknown): string {
  try {
    const encoded = JSON.stringify(value);
    return typeof encoded === 'string' && encoded.length > 0 ? encoded : '{}';
  } catch {
    return '{}';
  }
}

function validationRefusal(
  command: AnyCommand,
  rawIssues: readonly RuntimeIssue[],
): Refusal {
  const issues = rawIssues.slice(0, MAX_ISSUES_REPORTED);
  const overflow = rawIssues.length - issues.length;

  const wrong: string[] = [];
  const expected: string[] = [];
  for (let i = 0; i < issues.length; i += 1) {
    const issue = issues[i];
    if (!issue) continue;
    wrong.push(`${pathOf(issue)}: ${messageOf(issue)}`);
    expected.push(`${pathOf(issue)}: ${expectationOf(issue)}`);
  }
  if (overflow > 0) wrong.push(`and ${overflow} more field(s)`);

  return refuse(
    [
      `${command.name} was called with arguments that do not match its schema, so nothing ran.`,
      `What went wrong. ${wrong.join(' | ')}`,
      `What was expected. ${expected.join(' | ')}`,
      `Example of a call that would validate. ${exampleCallFor(command, issues)}`,
    ].join('\n'),
    { retryable: true },
  );
}

// ---------------------------------------------------------------------------
// Diff checks
//
// The base version pick used to live here as a second implementation. It is now
// `readBaseVersion` in `contract.ts`, which the writer re-checks against, so a
// mint and its check cannot drift apart again.
//
// THE EMPTY-CHANGE RULE IS A WRITE RULE. A read declares an empty diff because
// it changes nothing, so refusing an empty change list for every consequence
// refuses every read tool this surface has. The table and tenant checks are
// not write rules and still apply: they are what the witness card renders and
// what pins the call to the tenant it was scoped to.
// ---------------------------------------------------------------------------

function checkDiff(diff: Diff, tenant: TenantId, consequence: Consequence): Refusal | null {
  if (typeof diff.table !== 'string' || diff.table.length === 0) {
    return refuse('The command produced a change with no target table, so nothing was written.');
  }
  if (diff.tenant !== tenant) {
    return refuse(
      'The change was built for a different client than the one selected in this tab, so nothing was written.',
    );
  }
  if (consequence !== 'reads' && diff.changes.length === 0) {
    return refuse(
      'Nothing would change. The values given already match what is stored, so there is nothing to write.',
    );
  }
  const seen: Record<string, true> = {};
  for (let i = 0; i < diff.changes.length; i += 1) {
    const change = diff.changes[i];
    if (!change) continue;
    if (seen[change.field]) {
      return refuse(
        `The change lists ${change.field} twice with different values, so it is ambiguous and nothing was written.`,
      );
    }
    seen[change.field] = true;
    // A carried typed value and its rendering must describe the same thing. The
    // human approves `to` and the digest binds `value`, so a disagreement is an
    // approval for one change authorizing another.
    if (change.value !== undefined && change.to !== renderPatchValue(change.value)) {
      return refuse(
        `The change to ${change.field} does not render the value it would write, so the approval and the write describe two different things. Nothing was written.`,
      );
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Encoding
//
// Chrome would replace an empty result with "Operation succeeded", which is a
// lie for a refusal, so every path here returns a non-empty string. The
// receipt encoder reads only the declared fields of `Receipt`; a service
// response is never spread in, and primitive-only is not treated as safe on
// its own because a secret is a string too.
// ---------------------------------------------------------------------------

function flatten(value: string): string {
  return clip(value.replace(/\s+/g, ' ').trim(), MAX_FACT_CHARS);
}

function clip(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, Math.max(0, max - 1))}…`;
}

function truncate(text: string, max: number): string {
  const body = text.trim().length > 0 ? text : 'The command finished with no detail to report.';
  if (body.length <= max) return body;
  const notice = `\n[truncated to ${max} characters]`;
  const keep = Math.max(0, max - notice.length);
  return `${body.slice(0, keep).replace(/\s+$/, '')}${notice}`;
}

function encodeReceipt(command: AnyCommand, receipt: Receipt, max: number): string {
  const lines: string[] = [];

  const summary = typeof receipt.summary === 'string' ? receipt.summary.trim() : '';
  lines.push(summary.length > 0 ? summary : `${command.name} completed.`);

  const facts = receipt.facts;
  if (facts) {
    const keys = Object.keys(facts).sort();
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (key === undefined) continue;
      const value = facts[key];
      // Defensive as well as typed. An untyped runtime object cannot smuggle a
      // nested payload through a field declared primitive.
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        lines.push(`${flatten(key)}: ${flatten(String(value))}`);
      }
    }
  }

  if (receipt.projectionFailed === true) {
    lines.push(
      'Warning. The write landed, but the backend could not project it into the generation inputs. ' +
        'Tell the operator the value is saved and may not reach content generation until it is reprojected.',
    );
  }

  return truncate(lines.join('\n'), max);
}

function encodeRefusal(refused: Refusal, max: number): string {
  const lines: string[] = [`Refused. ${refused.because}`];

  if (refused.remedy) {
    lines.push(`Next step. Call ${refused.remedy.command}. ${refused.remedy.why}`);
  }

  const candidates = refused.candidates;
  if (candidates && candidates.length > 0) {
    const shown = candidates.slice(0, MAX_CANDIDATES_REPORTED);
    const rendered: string[] = [];
    for (let i = 0; i < shown.length; i += 1) {
      const candidate = shown[i];
      if (!candidate) continue;
      rendered.push(`${flatten(candidate.id)} (${flatten(candidate.label)})`);
    }
    const more = candidates.length - shown.length;
    lines.push(
      `More than one row matched. Pick one of these ids and call again. ${rendered.join('; ')}` +
        (more > 0 ? ` and ${more} more` : ''),
    );
  }

  if (refused.retryable === true) {
    lines.push('This is temporary. The same call can be retried in a moment.');
  }

  return truncate(lines.join('\n'), max);
}

// ---------------------------------------------------------------------------
// The pipeline
// ---------------------------------------------------------------------------

export interface RuntimeOptions {
  /** Bounded wait covering both capability install and first selection. */
  readonly bootWindowMs?: number;
  readonly authTtlMs?: number;
  readonly maxOutputChars?: number;
  readonly logger?: RuntimeLogger;
  /** Injectable for tests. Nothing else reads the clock. */
  readonly now?: () => number;
}

const NEVER_ABORTED: AbortSignal = new AbortController().signal;

/**
 * Wraps one command as Chrome's `execute`. Every tool call flows through here
 * in this order: readiness, validate, scope, resolve, diff, focus, authorize,
 * run, encode.
 */
export function createExecutor<A, T>(
  command: Command<A, T>,
  options: RuntimeOptions = {},
): ToolExecute {
  const clock = options.now ?? Date.now;
  const log = options.logger ?? consoleLogger;
  const bootWindowMs = options.bootWindowMs ?? DEFAULT_BOOT_WINDOW_MS;
  const authTtlMs = options.authTtlMs ?? DEFAULT_AUTH_TTL_MS;
  const maxOutputChars = options.maxOutputChars ?? DEFAULT_MAX_OUTPUT_CHARS;
  // Only the four authored functions are generic. The encoders and the logger
  // read the command as a plain descriptor.
  const described: AnyCommand = command;

  return async function execute(rawArgs: unknown, context?: ToolExecuteContext): Promise<string> {
    const startedAt = clock();
    const signal = context?.signal ?? NEVER_ABORTED;
    let state: PipelineState = 'readiness';

    // What the operator's overlay is told, and the only thing this pipeline
    // publishes anywhere. Chrome's `toolactivated` carries a tool name and
    // nothing else, so the diff an operator watches an agent commit reaches
    // the witness from here or from nowhere.
    //
    // ONE EVENT PER INVOCATION, and never more than the confirmation already
    // renders. The diff, how the call ended, and the refusal the agent was
    // handed. Arguments the agent sent and bodies a service returned stay out,
    // because the overlay is read by whoever is looking at the screen.
    //
    // No id is published, deliberately. This runtime mints none, and the
    // journal settles the newest pending row for the tool name, which is the
    // row Chrome opened for this same call. Minting one here would name a row
    // that does not exist and the settle would be dropped.
    let settleDiff: Diff | null = null;
    let witnessed = false;
    const witness = (
      outcome: 'ok' | 'refused' | 'cancelled',
      refusal: string | null,
    ): void => {
      if (witnessed) return;
      witnessed = true;
      try {
        publishWitnessSettle({ toolName: described.name, outcome, diff: settleDiff, refusal });
      } catch {
        // A record is never a gate. With no window to dispatch on, the call
        // still returns exactly what it was going to return.
      }
    };

    const done = (outcome: PipelineOutcome, text: string): string => {
      log.debug('tool call finished', {
        tool: described.name,
        state,
        outcome,
        durationMs: clock() - startedAt,
        consequence: described.consequence,
      });
      return text;
    };
    const cancelled = (): string => {
      const refusal = refuse('The call was cancelled before it finished. Nothing was written.', {
        retryable: true,
      });
      witness('cancelled', refusal.because);
      return done('cancelled', encodeRefusal(refusal, maxOutputChars));
    };
    // Named `emitRefusal`, not `refuse`, so it cannot shadow the contract's
    // refusal constructor now that this module uses the shared one.
    const emitRefusal = (value: Refusal): string => {
      witness('refused', value.because);
      return done('refused', encodeRefusal(value, maxOutputChars));
    };

    try {
      // 1. Readiness. Registration runs before React mounts, so a call can
      //    land before the capability layer exists. Wait briefly, then refuse
      //    retryably. Never hang.
      if (signal.aborted) return cancelled();
      const readyDeadline = startedAt + bootWindowMs;
      const caps = await awaitCapabilities(readyDeadline, signal, clock);
      if (signal.aborted) return cancelled();
      if (!caps) {
        return emitRefusal(
          refuse(
            'Core is still starting up, so this tool is registered but not connected to the app yet. ' +
              'Retry in a moment.',
            { retryable: true },
          ),
        );
      }

      // 2. Validate. Chrome validates nothing, so the zod schema is the only
      //    enforced contract.
      state = 'validate';
      const parsed = command.args.safeParse(rawArgs);
      if (!parsed.success) {
        const issues = parsed.error.issues as unknown as readonly RuntimeIssue[];
        log.debug('arguments rejected', {
          tool: described.name,
          state,
          count: issues.length,
        });
        return emitRefusal(validationRefusal(described, issues));
      }

      // 3. Scope. Captured once and threaded through. The store is not read
      //    again below this line, because selection is per browser tab and the
      //    operator can switch client mid call.
      state = 'scope';
      const withinBootWindow = clock() - MODULE_LOADED_AT < bootWindowMs;
      const scope = await requireScope(
        withinBootWindow ? readyDeadline : clock(),
        signal,
        clock,
      );
      if (isRefusal(scope)) return emitRefusal(scope);
      if (signal.aborted) return cancelled();
      const tenant: TenantId = scope;

      // 4. Resolve. Reads only.
      state = 'resolve';
      const target = await command.resolve({ args: parsed.data, tenant, read: caps.read, signal });
      if (signal.aborted) return cancelled();
      if (isRefusal(target)) return emitRefusal(target);

      // 5. Diff. Pure, synchronous, deterministic.
      state = 'diff';
      const diff = command.diff(target as T, tenant);
      const malformed = checkDiff(diff, tenant, described.consequence);
      if (malformed) return emitRefusal(malformed);
      // Published only once it has passed `checkDiff`. A diff this runtime has
      // declared malformed is not a change to show an operator as one.
      settleDiff = diff;

      // 6. Focus. The dirty-state veto, and the call site the widened
      //    `DirtyState` exists for. `dirtyVeto` fails closed on `unknown`, so a
      //    build in which no surface publishes refuses rather than overwriting
      //    an operator's open buffer. Read-only commands skip it: they cannot
      //    clobber anything, and vetoing them would make the surface useless
      //    exactly when the operator is mid-edit and most wants to ask about it.
      if (described.consequence !== 'reads') {
        state = 'focus';
        const veto = dirtyVeto(caps.read.dirtySections(tenant), described.section);
        if (veto) return emitRefusal(veto);
      }

      // 7. Authorize. Not a consent gate. A binding to this exact target, so
      //    an authorization cannot be replayed against another row.
      state = 'authorize';
      let baseVersion: string | null = null;
      try {
        const row = await caps.read.currentRow(
          diff.table,
          tenant,
          diff.rowId === null ? undefined : diff.rowId,
        );
        baseVersion = readBaseVersion(row);
      } catch (error) {
        // Best effort by design. There is no server side compare and swap on
        // these rows yet, so a missing base version weakens the write, it does
        // not invalidate it.
        log.debug('base version unavailable', {
          tool: described.name,
          state,
          errorName: errorNameOf(error),
        });
      }
      if (signal.aborted) return cancelled();

      const record = mintAuthorization(
        {
          tenant,
          table: diff.table,
          rowId: diff.rowId,
          patchDigest: computePatchDigest(patchFromDiff(diff)),
          baseVersion,
        },
        authTtlMs,
        clock,
      );

      // 8. Run. The only writing step.
      state = 'run';
      log.debug('committing', {
        tool: described.name,
        state,
        consequence: described.consequence,
        reachesGeneratedContent: diff.reachesGeneratedContent,
        count: diff.changes.length,
      });
      const receipt = await command.run(target as T, record.auth, caps.write);

      // 9. Encode. Closed shape. Declared fields of Receipt only.
      state = 'encode';
      if (isRefusal(receipt)) return emitRefusal(receipt);

      // READ COMMANDS ARE EXEMPT, and the exemption is what makes them
      // reachable. A read reaches no write seam, so its record is untouched by
      // design and this invariant would refuse every read that succeeded.
      if (described.consequence !== 'reads' && !record.consumed) {
        // AN INVARIANT FAILURE, not a warning that returns success. Every
        // installed write seam spends its authorization at the boundary
        // (`wrapWriteSeams`), so a successful return with this record untouched
        // means the command reported a write that never went through that
        // boundary. Burn the record so it cannot be replayed, and refuse:
        // announcing a write we cannot substantiate is the one outcome worse
        // than declining one that happened.
        record.consumed = true;
        log.error('successful run never consumed its authorization', {
          tool: described.name,
          state,
          consequence: described.consequence,
        });
        return emitRefusal(
          refuse(
            `${described.name} reported success without spending its authorization, so this surface cannot confirm that what was written is what was approved. Check the record in Core before running it again.`,
            { retryable: false },
          ),
        );
      }

      witness('ok', null);
      return done('ok', encodeReceipt(described, receipt, maxOutputChars));
    } catch (error) {
      // Everything reaching here is a bug. Refusals are values now: `commit`
      // and `run` both return `Receipt | Refusal`, so there is no longer a
      // refusal-carrying exception to unwrap. A bug must not escape into
      // Chrome, where an async rejection reaches no React error boundary. The
      // message is never logged or returned because it can carry a service
      // response.
      log.error('tool call threw', {
        tool: described.name,
        state,
        outcome: 'failed',
        durationMs: clock() - startedAt,
        errorName: errorNameOf(error),
      });
      const refusal = refuse(
        `${described.name} hit an unexpected error during the ${state} step and stopped. ` +
          'Tell the operator to check the browser console, and do not retry more than once.',
        { retryable: true },
      );
      // The agent is handed a refusal, so the witness is told the same thing.
      // There is no `failed` on that channel, and inventing one would mean the
      // card said something the agent never saw.
      witness('refused', refusal.because);
      return encodeRefusal(refusal, maxOutputChars);
    }
  };
}

function errorNameOf(error: unknown): string {
  if (error instanceof Error) return error.name;
  return typeof error;
}

// ---------------------------------------------------------------------------
// The install seam
//
// `installAgentSurface(catalog, deps)` derives `name`, `description` and
// `annotations` itself and asks its caller only for the two members it cannot
// derive. This is that provider. Keeping `buildBody` unable to supply a name or
// an annotation is what makes annotation derivation structural rather than a
// convention a future author can quietly break.
// ---------------------------------------------------------------------------

/**
 * The JSON Schema Chrome forwards to the model.
 *
 * Advisory only. Chromium's own IDL records that conformance "is currently not
 * enforced upon tool execution", so this is guidance aimed at the model and the
 * zod schema in step 2 remains the only gate. `unrepresentable: 'any'` keeps a
 * schema that JSON Schema cannot express from failing the whole catalog build
 * over a hint.
 *
 * The cast is because zod returns its own `JSONSchema.BaseSchema` interface,
 * which has no index signature and so is not assignable to
 * `WebMCP.JsonSchema`. Chrome only `JSON.stringify`s the value.
 */
function inputSchemaFor(command: AnyCommand): WebMCP.JsonSchema | undefined {
  const schema = z.toJSONSchema(command.args, {
    io: 'input',
    unrepresentable: 'any',
  }) as unknown as WebMCP.JsonSchema;
  return schema;
}

/**
 * Builds the `InstallDeps.buildBody` provider.
 *
 * Deliberately returns only `{ inputSchema, execute }`. It cannot reach `name`,
 * `description` or `annotations`, so no future author can hand-author a
 * `readOnlyHint` that contradicts the command's declared `consequence`.
 */
export function createToolBodyBuilder(
  options: RuntimeOptions = {},
): (command: AnyCommand) => ToolBody {
  return function buildBody(command: AnyCommand): ToolBody {
    const inputSchema = inputSchemaFor(command);
    return {
      ...(inputSchema === undefined ? {} : { inputSchema }),
      execute: createExecutor(command, options),
    };
  };
}
